/**
 * Tags Module - Simplified UI
 * Manages tag categories and tags for report grouping and statistics
 */

import { apiCall } from '../core/api.js';
import { auth } from '../core/auth.js';
import { showError, showSuccess } from '../core/ui.js';
import { getCurrentUser } from '../utils/helpers.js';
import { t } from '../core/i18n.js';

/**
 * State management for tags
 */
const tagsState = {
    structure: [],
    nextNodeId: 1,
    expandedCategories: new Set(),
    accountId: null,
    hasUnsavedChanges: false,
    autoSaveTimeout: null
};

/**
 * Show tags editor
 */
export async function showTags() {
    const container = document.getElementById('tags-view');
    if (!container) {
        console.error('Tags view container not found');
        return;
    }
    
    const user = getCurrentUser();
    if (!user || !user.account_id) {
        showError('Please login to manage tags');
        return;
    }
    
    tagsState.accountId = user.account_id;
    
    // Load existing structure
    await loadTags();
    
    // Render the editor
    renderTagsEditor();
}

/**
 * Load tags from server
 */
async function loadTags() {
    try {
        const response = await apiCall(`/tags/structure?account_id=${tagsState.accountId}`, {
            headers: auth.getAuthHeaders()
        });
        
        if (response && response.structure) {
            tagsState.structure = response.structure;
            tagsState.nextNodeId = Math.max(...getAllNodeIds(response.structure), 0) + 1;
        } else {
            tagsState.structure = [];
            tagsState.nextNodeId = 1;
        }
    } catch (error) {
        console.error('Error loading tags:', error);
        tagsState.structure = [];
        tagsState.nextNodeId = 1;
    }
}

/**
 * Render the tags editor
 */
function renderTagsEditor() {
    const container = document.getElementById('tags-view');
    if (!container) return;
    
    container.innerHTML = `
        <div class="h-full p-6 overflow-y-auto">
            <div class="max-w-4xl mx-auto">
                <div class="flex items-center justify-between mb-6">
                    <div>
                        <h3 class="text-lg font-medium text-gray-900">${t('tags.title')}</h3>
                        <p class="text-sm text-gray-500 mt-1">${t('tags.description')}</p>
                    </div>
                    <div class="flex items-center space-x-3">
                        <button onclick="window.addTagCategory()" 
                                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700">
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                            </svg>
                            ${t('tags.addCategory')}
                        </button>
                        <button id="save-tags-btn" onclick="window.saveTags()" 
                                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700">
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                            </svg>
                            ${t('tags.saveAll')}
                        </button>
                    </div>
                </div>
                
                <div id="tags-categories" class="space-y-3">
                    <!-- Categories will be rendered here -->
                </div>
            </div>
        </div>
    `;
    
    renderTagsCategories();
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * Update save button state based on unsaved changes
 */
function updateSaveButtonState() {
    const saveBtn = document.getElementById('save-tags-btn');
    if (!saveBtn) return;
    
    if (tagsState.hasUnsavedChanges) {
        // Unsaved changes - show normal Save All button
        saveBtn.disabled = false;
        saveBtn.className = 'inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700';
        saveBtn.innerHTML = `
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            ${t('tags.saveAll')}
        `;
    } else {
        // Everything saved - show Saved! state
        saveBtn.disabled = true;
        saveBtn.className = 'inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600';
        saveBtn.innerHTML = `
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            ${t('tags.saved')}
        `;
    }
}

/**
 * Trigger auto-save after changes (debounced)
 */
function triggerAutoSave() {
    // Clear any pending auto-save
    if (tagsState.autoSaveTimeout) {
        clearTimeout(tagsState.autoSaveTimeout);
    }
    
    // Mark as unsaved immediately
    tagsState.hasUnsavedChanges = true;
    updateSaveButtonState();
    
    // Auto-save after 1 second of no changes
    tagsState.autoSaveTimeout = setTimeout(async () => {
        await window.saveTags();
    }, 1000);
}

/**
 * Set up warning when leaving page with unsaved changes
 */
function setupUnsavedChangesWarning() {
    window.addEventListener('beforeunload', (e) => {
        if (tagsState.hasUnsavedChanges) {
            e.preventDefault();
            e.returnValue = ''; // Required for Chrome
            return ''; // Some browsers show this message
        }
    });
}

// ============================================================================
// Rendering Functions
// ============================================================================

/**
 * Render all categories
 */
function renderTagsCategories() {
    const container = document.getElementById('tags-categories');
    if (!container) return;
    
    if (tagsState.structure.length === 0) {
        container.innerHTML = `
            <div class="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                <p class="mt-2 text-sm text-gray-500">No tag categories yet</p>
                <p class="text-xs text-gray-400">Click "Add Category" to start creating tags for reports</p>
            </div>
        `;
        return;
    }
    
    container.innerHTML = tagsState.structure.map(category => renderCategory(category)).join('');
}

/**
 * Render a single category with its tags
 */
function renderCategory(category) {
    const isExpanded = tagsState.expandedCategories.has(category.id);
    const tags = category.children || [];
    
    return `
        <div class="bg-white border border-gray-200 rounded-lg shadow-sm">
            <!-- Category Header -->
            <div class="flex items-center p-4 cursor-pointer hover:bg-gray-50" onclick="window.toggleTagCategory(${category.id})">
                <svg class="w-5 h-5 mr-3 text-gray-400 transform transition-transform ${isExpanded ? 'rotate-90' : ''}" 
                     fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
                <svg class="w-5 h-5 mr-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                <input type="text" 
                       value="${escapeHtml(category.name)}" 
                       onclick="event.stopPropagation()"
                       onchange="window.updateTagCategoryName(${category.id}, this.value)"
                       class="flex-1 px-2 py-1 border-0 focus:border-b-2 focus:border-primary-500 focus:outline-none text-sm font-medium text-gray-900"
                       placeholder="Category name">
                <span class="text-xs text-gray-400 mr-4">${tags.length} tag${tags.length !== 1 ? 's' : ''}</span>
                <button onclick="event.stopPropagation(); window.deleteTagCategory(${category.id})" 
                        class="p-1 text-gray-400 hover:text-red-600"
                        title="Delete category">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                </button>
            </div>
            
            <!-- Category Content (expandable) -->
            <div class="border-t border-gray-200 p-4 space-y-2 ${isExpanded ? '' : 'hidden'}" id="category-${category.id}-content">
                ${tags.map(tag => renderTagItem(category.id, tag)).join('')}
                ${renderNewTagInput(category.id)}
            </div>
        </div>
    `;
}

/**
 * Render a tag item
 */
function renderTagItem(categoryId, tag) {
    const tagColor = tag.color || '#6B7280';
    return `
        <div class="flex items-center space-x-2 group">
            <svg class="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
            <input type="text" 
                   value="${escapeHtml(tag.name || '')}"
                   onchange="window.updateTagName(${categoryId}, ${tag.id}, this.value)"
                   class="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                   placeholder="Tag name">
            <input type="color" 
                   value="${tagColor}"
                   onchange="window.updateTagColor(${categoryId}, ${tag.id}, this.value)"
                   class="w-12 h-9 border border-gray-300 rounded cursor-pointer"
                   title="Tag color">
            <div class="inline-flex items-center px-2 py-1 rounded text-xs font-medium" 
                 style="background-color: ${tagColor}20; color: ${tagColor}; border: 1px solid ${tagColor}40;">
                ${escapeHtml(tag.name || 'Tag')}
            </div>
            <button onclick="window.deleteTag(${categoryId}, ${tag.id})" 
                    class="p-2 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Delete tag">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
        </div>
    `;
}

/**
 * Render new tag input field
 */
function renderNewTagInput(categoryId) {
    return `
        <div class="flex items-center space-x-2 pt-2">
            <svg class="w-4 h-4 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            <input type="text" 
                   id="new-tag-${categoryId}"
                   oninput="window.handleNewTagInput(${categoryId}, this)"
                   onkeydown="if(event.key==='Enter') { event.preventDefault(); window.addNewTag(${categoryId}); }"
                   class="flex-1 px-3 py-2 border border-dashed border-gray-300 rounded-md text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                   placeholder="Type new tag name and press Enter or click OK...">
            <input type="color" 
                   id="new-tag-color-${categoryId}"
                   value="#3B82F6"
                   class="w-12 h-9 border border-gray-300 rounded cursor-pointer"
                   title="Tag color"
                   style="display: none;">
            <button id="add-tag-btn-${categoryId}" 
                    onclick="window.addNewTag(${categoryId})" 
                    style="display: none;"
                    class="px-3 py-2 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 whitespace-nowrap"
                    title="Add tag">
                OK
            </button>
        </div>
    `;
}

// ============================================================================
// Event Handlers
// ============================================================================

/**
 * Add new category
 */
window.addTagCategory = function() {
    const newCategory = {
        id: tagsState.nextNodeId++,
        name: 'New Category',
        is_category: true,
        children: [],
        order: tagsState.structure.length,
        color: null
    };
    
    tagsState.structure.push(newCategory);
    tagsState.expandedCategories.add(newCategory.id);
    triggerAutoSave();
    renderTagsCategories();
    
    // Focus on the category name input
    setTimeout(() => {
        const inputs = document.querySelectorAll(`input[value="New Category"]`);
        if (inputs.length > 0) {
            inputs[inputs.length - 1].select();
        }
    }, 100);
};

/**
 * Toggle category expansion
 */
window.toggleTagCategory = function(categoryId) {
    if (tagsState.expandedCategories.has(categoryId)) {
        tagsState.expandedCategories.delete(categoryId);
    } else {
        tagsState.expandedCategories.add(categoryId);
    }
    renderTagsCategories();
};

/**
 * Update category name
 */
window.updateTagCategoryName = function(categoryId, newName) {
    const category = tagsState.structure.find(c => c.id === categoryId);
    if (category) {
        category.name = newName;
        triggerAutoSave();
    }
};

/**
 * Delete category
 */
window.deleteTagCategory = function(categoryId) {
    const category = tagsState.structure.find(c => c.id === categoryId);
    if (!category) return;
    
    const tagCount = category.children ? category.children.length : 0;
    const message = tagCount > 0
        ? `Delete "${category.name}" and its ${tagCount} tag${tagCount !== 1 ? 's' : ''}?`
        : `Delete "${category.name}"?`;
    
    if (!confirm(message)) return;
    
    tagsState.structure = tagsState.structure.filter(c => c.id !== categoryId);
    tagsState.expandedCategories.delete(categoryId);
    triggerAutoSave();
    renderTagsCategories();
};

/**
 * Handle new tag input
 */
window.handleNewTagInput = function(categoryId, input) {
    const btn = document.getElementById(`add-tag-btn-${categoryId}`);
    const colorPicker = document.getElementById(`new-tag-color-${categoryId}`);
    if (btn && colorPicker) {
        const hasValue = input.value.trim();
        btn.style.display = hasValue ? 'block' : 'none';
        colorPicker.style.display = hasValue ? 'block' : 'none';
    }
};

/**
 * Add new tag to category
 */
window.addNewTag = function(categoryId) {
    const input = document.getElementById(`new-tag-${categoryId}`);
    const colorPicker = document.getElementById(`new-tag-color-${categoryId}`);
    
    if (!input || !input.value.trim()) return;
    
    const category = tagsState.structure.find(c => c.id === categoryId);
    if (!category) return;
    
    if (!category.children) {
        category.children = [];
    }
    
    const newTag = {
        id: tagsState.nextNodeId++,
        name: input.value.trim(),
        is_category: false,
        children: [],
        order: category.children.length,
        color: colorPicker ? colorPicker.value : '#3B82F6'
    };
    
    category.children.push(newTag);
    triggerAutoSave();
    renderTagsCategories();
    
    // Keep category expanded
    tagsState.expandedCategories.add(categoryId);
};

/**
 * Update tag name
 */
window.updateTagName = function(categoryId, tagId, newName) {
    const category = tagsState.structure.find(c => c.id === categoryId);
    if (!category) return;
    
    const tag = category.children.find(t => t.id === tagId);
    if (tag) {
        tag.name = newName;
        triggerAutoSave();
        renderTagsCategories();
        // Keep category expanded
        tagsState.expandedCategories.add(categoryId);
    }
};

/**
 * Update tag color
 */
window.updateTagColor = function(categoryId, tagId, newColor) {
    const category = tagsState.structure.find(c => c.id === categoryId);
    if (!category) return;
    
    const tag = category.children.find(t => t.id === tagId);
    if (tag) {
        tag.color = newColor;
        triggerAutoSave();
        renderTagsCategories();
        // Keep category expanded
        tagsState.expandedCategories.add(categoryId);
    }
};

/**
 * Delete tag
 */
window.deleteTag = function(categoryId, tagId) {
    const category = tagsState.structure.find(c => c.id === categoryId);
    if (!category || !category.children) return;
    
    category.children = category.children.filter(t => t.id !== tagId);
    triggerAutoSave();
    renderTagsCategories();
    
    // Keep category expanded
    tagsState.expandedCategories.add(categoryId);
};

/**
 * Save all changes
 */
window.saveTags = async function() {
    const saveBtn = document.getElementById('save-tags-btn');
    if (!saveBtn || !tagsState.hasUnsavedChanges) return;
    
    try {
        // Show saving state
        saveBtn.disabled = true;
        saveBtn.className = 'inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gray-400 cursor-not-allowed';
        saveBtn.innerHTML = `
            <svg class="w-4 h-4 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Saving...
        `;
        
        await apiCall(`/tags/structure/${tagsState.accountId}`, {
            method: 'PUT',
            headers: auth.getAuthHeaders(),
            body: JSON.stringify({
                structure: tagsState.structure
            })
        });
        
        // Mark as saved
        tagsState.hasUnsavedChanges = false;
        updateSaveButtonState();
        
        showSuccess('Tags saved successfully');
        
    } catch (error) {
        console.error('Error saving tags:', error);
        showError('Failed to save tags: ' + (error.message || 'Unknown error'));
        
        // Reset button on error
        updateSaveButtonState();
    }
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get all node IDs from structure
 */
function getAllNodeIds(nodes) {
    let ids = [];
    for (const node of nodes) {
        ids.push(node.id);
        if (node.children && node.children.length > 0) {
            ids = ids.concat(getAllNodeIds(node.children));
        }
    }
    return ids;
}

// Initialize warning
setupUnsavedChangesWarning();
